import { paymentService } from "@/api/services/payment.service";
import { createServerFn } from "@tanstack/react-start";
import { getRequestHeaders } from "@tanstack/react-start/server";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";

function getClientIp(): string {
  try {
    const headers = getRequestHeaders();
    const forwardedFor = headers.get("x-forwarded-for");
    if (forwardedFor) {
      return forwardedFor.split(",")[0].trim();
    }
    return headers.get("x-real-ip") || "127.0.0.1";
  } catch {
    return "127.0.0.1";
  }
}

function sanitizePaymentError(_error: unknown): string {
  return "Online ödeme şu anda tamamlanamıyor. WhatsApp üzerinden satın alma veya Havale/EFT ile devam edebilirsiniz.";
}

const createProgramCheckoutSchema = z.object({
  programId: z.string(),
});

export const createIyzicoCheckoutSessionFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(createProgramCheckoutSchema)
  .handler(async ({ context, data }) => {
    const clientIp = getClientIp();

    try {
      const result = await paymentService.createProgramPaymentSession({
        userId: context.auth.userId,
        programId: data.programId,
        clientIp,
      });

      if (result.status === "failure") {
        return {
          status: 400,
          body: { error: sanitizePaymentError(result.errorMessage) },
        };
      }

      return {
        status: 200,
        body: {
          paymentId: result.paymentId,
          token: result.token,
          paymentPageUrl: result.paymentPageUrl,
          checkoutFormContent: result.checkoutFormContent,
        },
      };
    } catch (error) {
      console.error("createIyzicoCheckoutSessionFn error:", error);
      return {
        status: 500,
        body: { error: sanitizePaymentError(error) },
      };
    }
  });

const createPackageCheckoutSchema = z.object({
  packageId: z.string(),
  durationMonths: z.number().optional(),
});

export const createPackagePaymentSessionFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(createPackageCheckoutSchema)
  .handler(async ({ context, data }) => {
    const clientIp = getClientIp();

    try {
      const result = await paymentService.createPackagePaymentSession({
        userId: context.auth.userId,
        packageId: data.packageId,
        durationMonths: data.durationMonths,
        clientIp,
      });

      if (result.status === "failure") {
        return {
          status: 400,
          body: { error: sanitizePaymentError(result.errorMessage) },
        };
      }

      return {
        status: 200,
        body: {
          paymentId: result.paymentId,
          token: result.token,
          paymentPageUrl: result.paymentPageUrl,
          checkoutFormContent: result.checkoutFormContent,
        },
      };
    } catch (error) {
      console.error("createPackagePaymentSessionFn error:", error);
      return {
        status: 500,
        body: { error: sanitizePaymentError(error) },
      };
    }
  });

const createProductCheckoutSchema = z.object({
  productId: z.string(),
});

export const createProductPaymentSessionFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(createProductCheckoutSchema)
  .handler(async ({ context, data }) => {
    const clientIp = getClientIp();

    try {
      const result = await paymentService.createProductPaymentSession({
        userId: context.auth.userId,
        productId: data.productId,
        clientIp,
      });

      if (result.status === "failure") {
        return {
          status: 400,
          body: { error: sanitizePaymentError(result.errorMessage) },
        };
      }

      return {
        status: 200,
        body: {
          paymentId: result.paymentId,
          token: result.token,
          paymentPageUrl: result.paymentPageUrl,
          checkoutFormContent: result.checkoutFormContent,
        },
      };
    } catch (error) {
      console.error("createProductPaymentSessionFn error:", error);
      return {
        status: 500,
        body: { error: sanitizePaymentError(error) },
      };
    }
  });
